import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkles, Loader2, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const getAuthErrorMessage = (errorCode: string, errorDescription: string): { title: string; description: string } => {
  switch (errorCode) {
    case "otp_expired":
      return {
        title: "Link Expired",
        description: "Your verification link has expired. Please request a new one.",
      };
    case "access_denied":
      return {
        title: "Access Denied",
        description: errorDescription || "Access was denied. Please try again.",
      };
    case "invalid_request":
      return {
        title: "Invalid Request",
        description: "The request was invalid. Please try signing up again.",
      };
    case "user_not_found":
      return {
        title: "Account Not Found",
        description: "No account found with these credentials. Please sign up first.",
      };
    case "invalid_credentials":
      return {
        title: "Invalid Credentials",
        description: "The email or password you entered is incorrect.",
      };
    case "email_not_confirmed":
      return {
        title: "Email Not Verified",
        description: "Please check your email and click the verification link.",
      };
    default:
      return {
        title: "Authentication Error",
        description: errorDescription || "An error occurred. Please try again.",
      };
  }
};

const Auth = () => {
  const navigate = useNavigate();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [cooldownMinutes, setCooldownMinutes] = useState(0);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownMinutes > 0) {
      const timer = setTimeout(() => {
        setCooldownMinutes(prev => Math.max(0, prev - 1));
      }, 60000); // 1 minute
      return () => clearTimeout(timer);
    }
  }, [cooldownMinutes]);

  const handleResendEmail = async () => {
    if (!resendEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Email Required",
        description: "Please enter your email address.",
      });
      return;
    }

    setIsResending(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resend-verification-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email: resendEmail.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          // Rate limited
          if (data.retry_after_minutes) {
            setCooldownMinutes(data.retry_after_minutes);
          }
          toast({
            variant: "destructive",
            title: "Please Wait",
            description: data.message,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: data.message || "Failed to resend verification email.",
          });
        }
        return;
      }

      toast({
        title: "Email Sent!",
        description: data.message,
      });
      
      setShowResendForm(false);
      setResendEmail("");
    } catch (error) {
      console.error("Resend error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resend email. Please try again later.",
      });
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const checkSessionAndRedirect = async (userId: string) => {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('child_name')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error("Profile fetch error:", error);
      }
      
      if (!profile?.child_name) {
        navigate("/child-profile");
      } else {
        navigate("/home");
      }
    };

    // Check for auth errors in URL hash
    const hash = window.location.hash;
    if (hash && hash.includes("error=")) {
      const params = new URLSearchParams(hash.substring(1));
      const error = params.get("error");
      const errorCode = params.get("error_code");
      const errorDescription = params.get("error_description")?.replace(/\+/g, " ");

      if (error) {
        const { title, description } = getAuthErrorMessage(errorCode || "", errorDescription || "");
        
        toast({
          variant: "destructive",
          title,
          description,
        });

        // Show resend form for expired links
        if (errorCode === "otp_expired") {
          setShowResendForm(true);
        }

        // Clean up the URL hash
        window.history.replaceState(null, "", window.location.pathname);
        setIsCheckingSession(false);
        return;
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && event === 'SIGNED_IN') {
        setTimeout(() => {
          checkSessionAndRedirect(session.user.id);
        }, 0);
      }
      
      // Handle session expiry
      if (event === 'TOKEN_REFRESHED' && !session) {
        toast({
          variant: "destructive",
          title: "Session Expired",
          description: "Your session has expired. Please log in again.",
        });
        setIsCheckingSession(false);
      }
    });

    // Check if URL contains auth tokens (email confirmation redirect)
    const hasAuthTokens = window.location.hash.includes('access_token') || 
                          window.location.hash.includes('refresh_token') ||
                          window.location.hash.includes('type=');

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        toast({
          variant: "destructive",
          title: "Session Error",
          description: "Failed to check your session. Please try logging in again.",
        });
        setIsCheckingSession(false);
        return;
      }
      
      if (session) {
        checkSessionAndRedirect(session.user.id);
      } else if (!hasAuthTokens) {
        setIsCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-primary rounded-2xl mb-4">
            <Sparkles className="w-7 h-7 md:w-8 md:h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-4">StoryPlay Kids</h1>
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-primary rounded-2xl mb-4">
            <Sparkles className="w-7 h-7 md:w-8 md:h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">StoryPlay Kids</h1>
          <p className="text-sm md:text-base text-muted-foreground">Create magical stories with AI</p>
        </div>
        
        <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8 border border-border">
          {showResendForm ? (
            <div className="space-y-4">
              <div className="text-center">
                <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-foreground mb-2">Resend Verification Email</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll send you a new verification link.
                </p>
              </div>
              
              <div className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={isResending || cooldownMinutes > 0}
                />
                
                <Button
                  className="w-full"
                  onClick={handleResendEmail}
                  disabled={isResending || cooldownMinutes > 0}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : cooldownMinutes > 0 ? (
                    `Wait ${cooldownMinutes} min`
                  ) : (
                    "Send Verification Email"
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowResendForm(false)}
                >
                  Back to Login
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                Rate limit: 1 email per 5 minutes, 10 per day
              </p>
            </div>
          ) : (
            <>
              <SupabaseAuth
                supabaseClient={supabase}
                appearance={{
                  theme: ThemeSupa,
                  variables: {
                    default: {
                      colors: {
                        brand: 'hsl(var(--primary))',
                        brandAccent: 'hsl(var(--primary))',
                      },
                    },
                  },
                }}
                providers={["google"]}
                redirectTo={`${window.location.origin}/auth`}
              />
              
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  onClick={() => setShowResendForm(true)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Didn't receive verification email? <span className="underline">Resend</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;